import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth, UserButton } from "@clerk/react";
import { HugeiconsIcon } from "@hugeicons/react";
import { AnimatePresence, motion } from "motion/react";
import { Menu01Icon, Cancel01Icon } from "@hugeicons/core-free-icons";
import {
  Workflow,
  LayoutGrid,
  Zap,
  Rss,
  List,
  MessageCircle,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import logo from "@/assets/png/logo.png";
import { SignInModal } from "@/components/auth/SignInModal";
import { WaitlistModal } from "@/components/auth/WaitlistModal";

interface DropdownItem {
  icon: LucideIcon;
  title: string;
  description: string;
  href: string;
  external?: boolean;
}

interface DropdownSection {
  heading: string;
  items: DropdownItem[];
}

interface NavDropdownDef {
  id: string;
  label: string;
  type: "dropdown";
  sections: DropdownSection[];
}

interface NavLinkDef {
  id: string;
  label: string;
  type: "link";
  href: string;
}

type NavItemDef = NavDropdownDef | NavLinkDef;

const navItems: NavItemDef[] = [
  { id: "home", label: "Home", type: "link", href: "/" },
  {
    id: "product",
    label: "Product",
    type: "dropdown",
    sections: [
      {
        heading: "BUILD",
        items: [
          {
            icon: Workflow,
            title: "Features",
            description: "Visual AI pipeline builder",
            href: "/features",
          },
          {
            icon: LayoutGrid,
            title: "Templates",
            description: "Pre-built workflow templates",
            href: "/templates",
          },
        ],
      },
      {
        heading: "CONNECT",
        items: [
          {
            icon: Zap,
            title: "Integrations",
            description: "Connect your favorite AI models",
            href: "/integrations",
          },
        ],
      },
    ],
  },
  {
    id: "resources",
    label: "Resources",
    type: "dropdown",
    sections: [
      {
        heading: "LEARN",
        items: [
          {
            icon: Rss,
            title: "Blog",
            description: "Tips and insights",
            href: "/blog",
          },
          {
            icon: List,
            title: "Changelog",
            description: "See what's new",
            href: "/changelog",
          },
        ],
      },
      {
        heading: "GET HELP",
        items: [
          {
            icon: MessageCircle,
            title: "Support",
            description: "Get help when you need it",
            href: "/support",
          },
        ],
      },
    ],
  },
  { id: "pricing", label: "Pricing", type: "link", href: "/pricing" },
  // { id: "changelog", label: "Changelog", type: "link", href: "/changelog" },
];

const dropdownIds = navItems
  .filter((item) => item.type === "dropdown")
  .map((item) => item.id);

function getDirection(from: string | null, to: string | null): number {
  if (!from || !to) return 0;
  const fromIdx = dropdownIds.indexOf(from);
  const toIdx = dropdownIds.indexOf(to);
  if (fromIdx === -1 || toIdx === -1) return 0;
  return toIdx > fromIdx ? 1 : -1;
}

const contentVariants = {
  enter: (dir: number) => ({ opacity: 0, x: dir * 20 }),
  center: { opacity: 1, x: 0 },
  exit: (dir: number) => ({ opacity: 0, x: dir * -20 }),
};

export function NavbarV2() {
  const [blurred, setBlurred] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const [signUpOpen, setSignUpOpen] = useState(false);
  const { isSignedIn, isLoaded } = useAuth();
  const location = useLocation();

  const prevDropdownRef = useRef<string | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const direction = getDirection(prevDropdownRef.current, activeDropdown);

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      prevDropdownRef.current = null;
      setActiveDropdown(null);
    }, 150);
  };

  const handleDropdownEnter = (id: string) => {
    cancelClose();
    if (activeDropdown !== id) {
      prevDropdownRef.current = activeDropdown;
      setActiveDropdown(id);
    }
  };

  useEffect(() => {
    const onScroll = () => {
      setBlurred(window.scrollY > 1);
      setScrolled(window.scrollY > 500);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    prevDropdownRef.current = null;
    setActiveDropdown(null);
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  const isHome = location.pathname === "/";
  const useHeroStyle = isHome && !scrolled && !mobileOpen;

  const activeDropdownDef = navItems.find(
    (item): item is NavDropdownDef =>
      item.id === activeDropdown && item.type === "dropdown",
  );

  return (
    <nav
      className={cn(
        "fixed top-0 right-0 left-0 z-50 transition-all duration-300",
        mobileOpen
          ? "bg-background border-border border-b"
          : scrolled || (!isHome && blurred)
            ? "bg-background border-border border-b backdrop-blur-xl"
            : blurred
              ? "backdrop-blur-md bg-transparent"
              : "bg-transparent",
      )}
    >
      <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-6 relative">
        {/* Logo */}
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <img
            src={logo}
            alt="Rayloop"
            className={cn("h-7", useHeroStyle ? "invert" : "dark:invert")}
          />
          <span
            className={cn(
              "text-lg font-semibold tracking-tight",
              useHeroStyle ? "text-white" : "text-foreground",
            )}
          >
            Rayloop
          </span>
        </Link>

        {/* Desktop nav — absolutely centered so it's independent of logo/auth widths */}
        <div className="absolute left-1/2 -translate-x-1/2 hidden items-center gap-1 md:flex">
          {navItems.map((item) => {
            if (item.type === "link") {
              return (
                <Link
                  key={item.id}
                  to={item.href}
                  className={cn(
                    "rounded-full px-4 py-2 text-base font-medium transition-colors",
                    useHeroStyle
                      ? location.pathname === item.href
                        ? "text-white bg-white/10"
                        : "text-white hover:bg-white/8"
                      : location.pathname === item.href
                        ? "text-foreground bg-foreground/10"
                        : "text-foreground hover:bg-foreground/8",
                  )}
                >
                  {item.label}
                </Link>
              );
            }
            return (
              <button
                key={item.id}
                onMouseEnter={() => handleDropdownEnter(item.id)}
                onMouseLeave={scheduleClose}
                className={cn(
                  "cursor-default rounded-full px-4 py-2 text-base font-medium transition-colors",
                  useHeroStyle
                    ? activeDropdown === item.id
                      ? "text-white bg-white/10"
                      : "text-white hover:bg-white/8"
                    : activeDropdown === item.id
                      ? "text-foreground bg-foreground/10"
                      : "text-foreground hover:bg-foreground/8",
                )}
              >
                {item.label}
              </button>
            );
          })}
        </div>

        {/* Desktop auth */}
        <div className="hidden items-center gap-3 md:flex">
          {isLoaded && isSignedIn ? (
            <div className="flex items-center gap-3">
              <button
                className={cn(
                  "rounded-full px-4 py-2 text-base font-medium transition-colors cursor-pointer",
                  useHeroStyle
                    ? "text-white hover:bg-white/8"
                    : "text-foreground hover:bg-foreground/8",
                )}
              >
                <Link to="/dashboard">Dashboard</Link>
              </button>
              <UserButton appearance={{ elements: { avatarBox: "h-7 w-7" } }} />
            </div>
          ) : (
            <>
              <button
                className={cn(
                  "rounded-full px-4 py-2 text-base font-medium transition-colors cursor-pointer",
                  useHeroStyle
                    ? "text-white hover:bg-white/8"
                    : "text-foreground hover:bg-foreground/8",
                )}
                onClick={() => setSignInOpen(true)}
              >
                Sign in
              </button>
              <Button
                className="bg-brand hover:bg-brand/90 cursor-pointer rounded-lg text-white"
                onClick={() => setSignUpOpen(true)}
              >
                Get Started
              </Button>
            </>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          className="text-foreground md:hidden"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <HugeiconsIcon
            icon={mobileOpen ? Cancel01Icon : Menu01Icon}
            className="size-5"
          />
        </button>
      </div>

      {/* Mega dropdown panel */}
      <AnimatePresence>
        {activeDropdown && activeDropdownDef && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18, ease: [0.25, 0.1, 0.25, 1] }}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            className="absolute left-0 right-0 top-16 hidden md:block"
          >
            <div
              className={cn(
                "mx-auto px-6 pb-2",
                activeDropdownDef.sections.length === 1
                  ? "max-w-xs"
                  : "max-w-xl",
              )}
            >
              <div className="bg-card border-border overflow-hidden rounded-2xl border shadow-2xl backdrop-blur-xl">
                <AnimatePresence mode="wait" custom={direction}>
                  <motion.div
                    key={activeDropdown}
                    custom={direction}
                    variants={contentVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{
                      duration: 0.14,
                      ease: [0.25, 0.1, 0.25, 1],
                    }}
                    className={cn(
                      "p-4",
                      activeDropdownDef.sections.length === 1
                        ? "grid grid-cols-1"
                        : "grid grid-cols-2",
                    )}
                  >
                    {activeDropdownDef.sections.map((section, idx) => (
                      <div
                        key={section.heading}
                        className={cn(
                          "space-y-0.5",
                          idx === 1 && "border-border border-l pl-5",
                        )}
                      >
                        <p className="text-muted-foreground/50 mb-2.5 px-2 text-[10px] font-semibold tracking-[0.1em] uppercase">
                          {section.heading}
                        </p>
                        {section.items.map((dropItem) => {
                          const Icon = dropItem.icon;
                          const itemClass =
                            "group flex items-center gap-2.5 rounded-lg px-2 py-2 transition-colors hover:bg-accent/50";
                          const inner = (
                            <>
                              <div className="bg-muted group-hover:bg-accent flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors">
                                <Icon className="text-muted-foreground size-3.5" />
                              </div>
                              <div>
                                <p className="text-foreground text-sm font-medium leading-tight">
                                  {dropItem.title}
                                </p>
                                <p className="text-muted-foreground text-[11px] leading-tight mt-0.5">
                                  {dropItem.description}
                                </p>
                              </div>
                            </>
                          );
                          return dropItem.external ? (
                            <a
                              key={dropItem.href}
                              href={dropItem.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={itemClass}
                            >
                              {inner}
                            </a>
                          ) : (
                            <Link
                              key={dropItem.href}
                              to={dropItem.href}
                              className={itemClass}
                            >
                              {inner}
                            </Link>
                          );
                        })}
                      </div>
                    ))}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="bg-background border-border border-t md:hidden">
          <div className="flex flex-col gap-1 px-6 py-4">
            {navItems.map((item) => {
              if (item.type === "link") {
                return (
                  <Link
                    key={item.id}
                    to={item.href}
                    onClick={() => setMobileOpen(false)}
                    className={cn(
                      "rounded-lg px-3 py-2 text-sm",
                      location.pathname === item.href
                        ? "text-brand bg-brand-muted font-medium"
                        : "text-foreground hover:bg-accent",
                    )}
                  >
                    {item.label}
                  </Link>
                );
              }
              return (
                <div key={item.id}>
                  <p className="text-muted-foreground mt-3 mb-1 px-3 text-xs font-medium uppercase tracking-wider">
                    {item.label}
                  </p>
                  {item.sections
                    .flatMap((s) => s.items)
                    .map((dropItem) => {
                      const Icon = dropItem.icon;
                      const mobileClass =
                        "text-foreground hover:bg-accent flex items-center gap-2 rounded-lg px-3 py-2 text-sm";
                      return dropItem.external ? (
                        <a
                          key={dropItem.href}
                          href={dropItem.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setMobileOpen(false)}
                          className={mobileClass}
                        >
                          <Icon className="text-muted-foreground size-4" />
                          {dropItem.title}
                        </a>
                      ) : (
                        <Link
                          key={dropItem.href}
                          to={dropItem.href}
                          onClick={() => setMobileOpen(false)}
                          className={mobileClass}
                        >
                          <Icon className="text-muted-foreground size-4" />
                          {dropItem.title}
                        </Link>
                      );
                    })}
                </div>
              );
            })}
            <div className="border-border mt-3 flex gap-3 border-t pt-4">
              {isLoaded && isSignedIn ? (
                <div className="flex w-full items-center justify-between">
                  <Button
                    variant="outline"
                    size="sm"
                    className="cursor-pointer"
                    asChild
                    onClick={() => setMobileOpen(false)}
                  >
                    <Link to="/dashboard">Dashboard</Link>
                  </Button>
                  <UserButton
                    appearance={{ elements: { avatarBox: "h-7 w-7" } }}
                  />
                </div>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 cursor-pointer"
                    onClick={() => {
                      setMobileOpen(false);
                      setSignInOpen(true);
                    }}
                  >
                    Sign in
                  </Button>
                  <Button
                    size="sm"
                    className="bg-brand hover:bg-brand/90 flex-1 cursor-pointer rounded-lg text-white"
                    onClick={() => {
                      setMobileOpen(false);
                      setSignUpOpen(true);
                    }}
                  >
                    Get Started
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <SignInModal open={signInOpen} onOpenChange={setSignInOpen} />
      <WaitlistModal open={signUpOpen} onOpenChange={setSignUpOpen} />
    </nav>
  );
}
